import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import UserServices from "../../UserServices/UserServices";
import "./NutritionMediaPicker.css";

const ACCEPTED = "image/jpeg,image/jpg,image/png,image/gif,image/webp";

export default function NutritionMediaPicker({
  show,
  onHide,
  onSelect,
  branchId,
  title = "Select image",
}) {
  const [tab, setTab] = useState("gallery");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const fileRef = useRef(null);

  const loadGallery = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const res = await UserServices.getNutritionGalleryImages({
        branch_id: branchId,
        search: search || undefined,
        limit: 120,
      });
      if (res.status === 200 && res.data?.status) {
        setImages(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
      toast.error("Could not load gallery");
    }
    setLoading(false);
  }, [branchId, search]);

  useEffect(() => {
    if (show) {
      setTab("gallery");
      setSelectedId(null);
      loadGallery();
    }
  }, [show, loadGallery]);

  const handleUpload = async (fileList) => {
    const files = [...(fileList || [])].filter(Boolean);
    if (!files.length) return;

    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} is larger than 5MB`);
        return;
      }
    }

    setUploading(true);
    try {
      const res = await UserServices.uploadNutritionGalleryImages(branchId, files);
      if (res.status === 200 || res.status === 201) {
        const uploaded = res.data?.data || [];
        toast.success(res.data?.message || "Uploaded");
        await loadGallery();
        if (uploaded.length === 1 && onSelect) {
          onSelect({ imageUrl: uploaded[0].image_url, galleryId: uploaded[0].id });
          onHide();
        } else {
          setTab("gallery");
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || "Upload failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePickFromGallery = () => {
    const img = images.find((i) => i.id === selectedId);
    if (!img) {
      toast.info("Please select an image from the gallery");
      return;
    }
    onSelect?.({ imageUrl: img.image_url, galleryId: img.id });
    onHide();
  };

  const handleUploadAndUse = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      const res = await UserServices.uploadNutritionGalleryImages(branchId, [...files]);
      const uploaded = res.data?.data || [];
      if (uploaded[0] && onSelect) {
        onSelect({ imageUrl: uploaded[0].image_url, galleryId: uploaded[0].id });
        onHide();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="nutrition-media-picker-modal">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${tab === "gallery" ? "active" : ""}`}
              onClick={() => setTab("gallery")}
            >
              <i className="bi bi-images me-1" />
              Gallery
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${tab === "upload" ? "active" : ""}`}
              onClick={() => setTab("upload")}
            >
              <i className="bi bi-upload me-1" />
              Upload from computer
            </button>
          </li>
        </ul>

        {tab === "gallery" ? (
          <>
            <div className="d-flex gap-2 mb-3">
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search images..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadGallery()}
              />
              <Button variant="outline-secondary" size="sm" onClick={loadGallery}>
                Search
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="sm" /> Loading gallery...
              </div>
            ) : images.length === 0 ? (
              <div className="nutrition-media-empty text-center py-5">
                <i className="bi bi-image fs-1 text-muted d-block mb-2" />
                <p className="text-muted mb-2">No images in gallery yet.</p>
                <Button variant="primary" size="sm" onClick={() => setTab("upload")}>
                  Upload images
                </Button>
              </div>
            ) : (
              <div className="nutrition-media-grid">
                {images.map((img) => (
                  <button
                    key={img.id}
                    type="button"
                    className={`nutrition-media-item ${selectedId === img.id ? "selected" : ""}`}
                    onClick={() => setSelectedId(img.id)}
                    title={img.title || img.filename}
                  >
                    <img src={img.image_url} alt={img.title || "Gallery"} />
                    {selectedId === img.id ? (
                      <span className="nutrition-media-check">
                        <i className="bi bi-check-lg" />
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="nutrition-media-upload-zone text-center py-4">
            <i className="bi bi-cloud-upload fs-1 text-primary mb-3 d-block" />
            <p className="mb-3">Upload one or multiple images (JPG, PNG, GIF, WEBP — max 5MB each)</p>
            <input
              ref={fileRef}
              type="file"
              className="d-none"
              accept={ACCEPTED}
              multiple
              onChange={(e) => handleUpload(e.target.files)}
            />
            <Button
              variant="primary"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? "Uploading..." : "Choose files"}
            </Button>
            <p className="small text-muted mt-3 mb-0">
              Images are saved to your nutrition gallery and can be reused in meal plans.
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        {tab === "gallery" ? (
          <Button variant="primary" onClick={handlePickFromGallery} disabled={!selectedId}>
            Use selected image
          </Button>
        ) : (
          <label className="btn btn-outline-primary mb-0">
            {uploading ? "Uploading..." : "Upload & use first image"}
            <input
              type="file"
              className="d-none"
              accept={ACCEPTED}
              disabled={uploading}
              onChange={handleUploadAndUse}
            />
          </label>
        )}
      </Modal.Footer>
    </Modal>
  );
}
